import moment from "moment";
import {
  IDateParams,
  ISimpleParams,
  IStateParams,
  ITaxAnalysisReportParams,
  IYearMonthConciliarParams,
  IYearMonthParams,
} from "types";

moment.locale("es");

export const transformDocumentReportParams = (
  emails: string[],
  reportTypes: number[],
  simpleParams?: ISimpleParams,
  stateParams?: IStateParams
) => {
  
  const simple = simpleParams
    ? {
        types: simpleParams?.types,
        conditions: simpleParams?.conditions,
        documentEnd: simpleParams?.documentEnd,
        documentStart: simpleParams?.documentStart,
      }
    : undefined;

    const state = stateParams
    ? {
        conditions: stateParams?.conditions,
        documentEnd: stateParams?.documentEnd,
        documentStart: stateParams?.documentStart,
      }
    : undefined; 
  const reportParams = {
    emails,
    reportTypes,
    simple,
    state
  };
  return reportParams;
};

// Helper: vuelca todo el FormData a la consola de forma legible
function logFormData(formData: FormData, tag: string = "FormData") {
  const plain: Record<string, any[]> = {};
  // ¡OJO! Hay que iterar, no confiar en console.log(formData) directo
  formData.forEach((v, k) => {
    const val =
      v instanceof File
        ? { __file: true, name: v.name, size: v.size, type: v.type }
        : v;
    if (!plain[k]) plain[k] = [];
    plain[k].push(val);
  });
  console.group(`[${tag}] Dump`);
  Object.entries(plain).forEach(([k, arr]) => {
    console.debug(k, arr.length > 1 ? arr : arr[0]);
  });
  console.groupEnd();
}

export const transformTaxAnalysisReportParams = (
  user: string,
  reportTypes: number[],
  mailsToSend: string[],
  params: ITaxAnalysisReportParams
) => {
  console.group("[transformTaxAnalysisReportParams] start");

  // Logs de entrada
  console.debug("user:", user);
  console.debug("reportTypes:", reportTypes);
  console.debug("mailsToSend:", mailsToSend);
  console.debug("params?.load_tax_exel:", params?.load_tax_exel);

  const currentUserRequest = user;

  const safeCharges =
    (params &&
      params.load_tax_exel &&
      Array.isArray(params.load_tax_exel.charges) &&
      params.load_tax_exel.charges.filter((c: any) => c != null)) ||
    [];

  // Logs de archivos y charges
  const f1 = params?.load_tax_exel?.file1;
  const f2 = params?.load_tax_exel?.file2;

  console.debug(
    "file1:",
    f1 instanceof File ? { name: f1.name, size: f1.size, type: f1.type } : f1
  );
  console.debug(
    "file2:",
    f2 instanceof File ? { name: f2.name, size: f2.size, type: f2.type } : f2
  );
  console.debug("charges (raw):", params?.load_tax_exel?.charges);
  console.debug("charges (safe):", safeCharges);

  const excelTaxAnalysisParams = {
    reportType: 1,
    file1: f1,
    file2: f2,
    charges: safeCharges, // aseguramos []
  };

  const reports: any[] = [];
  const includesType1 = reportTypes.includes(1);
  console.debug("reportTypes.includes(1):", includesType1);
  if (includesType1) {
    reports.push(excelTaxAnalysisParams);
  }
  console.debug("reports length:", reports.length);

  const formData = new FormData();
  formData.append("currentUserRequest", currentUserRequest);
  console.debug("append currentUserRequest:", currentUserRequest);

  mailsToSend.forEach((mail, i) => {
    const key = `mailsToSend[${i}]`;
    formData.append(key, mail ?? "");
    console.debug("append", key, "=", mail);
  });

  reports.forEach((report, i) => {
    const base = `reports[${i}]`;

    formData.append(`${base}.reportType`, String(report.reportType));
    console.debug("append", `${base}.reportType`, "=", report.reportType);

    if (report.file1) {
      formData.append(`${base}.file1`, report.file1);
      console.debug("append", `${base}.file1`, "=", {
        name: report.file1.name,
        size: report.file1.size,
        type: report.file1.type,
      });
    } else {
      console.warn(`${base}.file1 ausente o falsy`);
    }

    if (report.file2) {
      formData.append(`${base}.file2`, report.file2);
      console.debug("append", `${base}.file2`, "=", {
        name: report.file2.name,
        size: report.file2.size,
        type: report.file2.type,
      });
    } else {
      console.warn(`${base}.file2 ausente o falsy`);
    }

    const charges: string[] = report.charges || [];
    console.debug(
      `${base}.charges length:`,
      charges.length,
      "values:",
      charges
    );
    charges.forEach((ch: string, j: number) => {
      const key = `${base}.charges[${j}]`;
      formData.append(key, ch ?? "");
      console.debug("append", key, "=", ch);
    });
  });

  // Dump final del FormData
  logFormData(formData, "transformTaxAnalysisReportParams");

  console.groupEnd();
  return formData;
};

export const transformRejectedReportParams = (
  emails: string[],
  states: number[],
  rejectedStart: Date | null,
  rejectedEnd: Date | null
) => {
  const reportParams = {
    emails,
    states,
    rejectedStart,
    rejectedEnd,
  };
  return reportParams;
};

export const transformRetentionReportParams = (
  user: string,
  reportTypes: number[],
  mailsToSend: string[],
  stateParams?: IStateParams,
  retentionParams?: IDateParams,
  accountingParams?: IDateParams,
  reclaimParams?: IDateParams,
  test1Params?: IDateParams,
  test2Params?: IDateParams,
  conciliarParams?: IYearMonthConciliarParams,
  otcParams?: IDateParams,
  rtrParams?: IDateParams
) => {
  const currentInquiryDate = moment(new Date())
    ?.format("yyyy-MM-DD hh:mm:ss")
    ?.toString();
  const currentUserRequest = user;
  const state = stateParams
    ? {
        reportType: 1,
        conditions: stateParams?.conditions,
        initialDate: moment(stateParams?.documentStart)
          ?.format("yyyy-MM-DD")
          ?.toString(),
        endDate: moment(stateParams?.documentEnd)
          ?.format("yyyy-MM-DD")
          ?.toString(),
      }
    : undefined;
  const retention = retentionParams
    ? {
        reportType: 2,
        conditions: null,
        initialDate: moment(retentionParams?.documentStart)
          ?.format("yyyy-MM-DD")
          ?.toString(),
        endDate: moment(retentionParams?.documentEnd)
          ?.format("yyyy-MM-DD")
          ?.toString(),
      }
    : undefined;
  const accounting = accountingParams
    ? {
        reportType: 3,
        conditions: null,
        initialDate: moment(accountingParams?.documentStart)
          ?.format("yyyy-MM-DD")
          ?.toString(),
        endDate: moment(accountingParams?.documentEnd)
          ?.format("yyyy-MM-DD")
          ?.toString(),
      }
    : undefined;
  const reclaim = reclaimParams
    ? {
        reportType: 4,
        conditions: null,
        initialDate: moment(reclaimParams?.documentStart)
          ?.format("yyyy-MM-DD")
          ?.toString(),
        endDate: moment(reclaimParams?.documentEnd)
          ?.format("yyyy-MM-DD")
          ?.toString(),
      }
    : undefined;
  const test1 = test1Params
    ? {
        reportType: 5,
        conditions: null,
        initialDate: moment(test1Params?.documentStart)
          ?.format("yyyy-MM-DD")
          ?.toString(),
        endDate: moment(test1Params?.documentEnd)
          ?.format("yyyy-MM-DD")
          ?.toString(),
      }
    : undefined;
  const test2 = test2Params
    ? {
        reportType: 6,
        conditions: null,
        initialDate: moment(test2Params?.documentStart)
          ?.format("yyyy-MM-DD")
          ?.toString(),
        endDate: moment(test2Params?.documentEnd)
          ?.format("yyyy-MM-DD")
          ?.toString(),
      }
    : undefined;
  const otc = otcParams
    ? {
        reportType: 8,
        conditions: null,
        initialDate: moment(otcParams?.documentStart)
          ?.format("yyyy-MM-DD")
          ?.toString(),
        endDate: moment(otcParams?.documentEnd)
          ?.format("yyyy-MM-DD")
          ?.toString(),
      }
    : undefined;
  const rtr = rtrParams
    ? {
        reportType: 9,
        conditions: null,
        initialDate: moment(rtrParams?.documentStart)
          ?.format("yyyy-MM-DD")
          ?.toString(),
        endDate: moment(rtrParams?.documentEnd)
          ?.format("yyyy-MM-DD")
          ?.toString(),
      }
    : undefined;
  const conciliar = conciliarParams
    ? {
        reportType: 7,
        conditions: null,
        inityear: conciliarParams?.yearMonthInit?.getFullYear()?.toString(),
        initmonth: (conciliarParams?.yearMonthInit?.getMonth() + 1)
          ?.toString()
          .padStart(2, "0"),
        endyear: conciliarParams?.yearMonthEnd?.getFullYear()?.toString(),
        endmonth: (conciliarParams?.yearMonthEnd?.getMonth() + 1)
          ?.toString()
          .padStart(2, "0"),
      }
    : undefined;

  const reports: any[] = [];
  if (reportTypes.includes(1)) {
    reports.push(state);
  }
  if (reportTypes.includes(2)) {
    reports.push(retention);
  }
  if (reportTypes.includes(3)) {
    reports.push(accounting);
  }
  if (reportTypes.includes(4)) {
    reports.push(reclaim);
  }
  if (reportTypes.includes(5)) {
    reports.push(test1);
  }
  if (reportTypes.includes(6)) {
    reports.push(test2);
  }
  if (reportTypes.includes(7)) {
    reports.push(conciliar);
  }
  if (reportTypes.includes(8)) {
    reports.push(otc);
  }
  if (reportTypes.includes(9)) {
    reports.push(rtr);
  }

  const reportParams = {
    currentInquiryDate,
    currentUserRequest,
    mailsToSend,
    reports,
  };
  return reportParams;
};
